import { GOAL_STATUSES, type GoalStatus } from '@ehr/shared';

/**
 * ehr#571 (PROGRESS_NOTE_10-20-V141, STATUS_PICKER_A): a status for each treatment plan goal and
 * objective this session, with an optional note and a dated status. Pure helpers for the note's
 * Treatment plan goals section, its printout and its tests.
 */

export const NOT_ADDRESSED: GoalStatus = 'Not addressed this session';

/** Every status but "Not addressed this session" carries a date (V69). */
export const statusDated = (s: string | undefined | null): boolean => !!s && s !== NOT_ADDRESSED;

export const isGoalStatus = (s: unknown): s is GoalStatus =>
  typeof s === 'string' && (GOAL_STATUSES as readonly string[]).includes(s);

/** The date field's label: Met, Not met and Discontinued keep their own wording. */
export function statusDateLabel(s: string | undefined): string {
  if (s === 'Met') return 'Date met';
  if (s === 'Not met') return 'Date not met';
  if (s === 'Discontinued') return 'Date discontinued';
  return 'Status date';
}

/** The pill's colour: green for In progress and Met, amber Partially met, red Not met, else grey (V91). */
export function statusTone(s: string | undefined): 'ok' | 'mid' | 'bad' | 'none' {
  if (s === 'In progress' || s === 'Met') return 'ok';
  if (s === 'Partially met') return 'mid';
  if (s === 'Not met') return 'bad';
  return 'none';
}

/** 'YYYY-MM-DD' as 'MM/DD/YYYY'; anything else as given. */
export function mdy(iso: string | undefined | null): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso ?? '');
  return m ? `${m[2]}/${m[3]}/${m[1]}` : (iso ?? '');
}

/** "Met · 10/09/2026", or just the status when it has no date. */
export function statusLabel(status: string | undefined, iso?: string): string {
  if (!status) return '';
  return iso && statusDated(status) ? `${status} · ${mdy(iso)}` : status;
}

export interface StatusRange {
  /** The day the linked plan was signed ('' when not known): no status is dated before it. */
  min: string;
  /** The later of today and the session date. */
  max: string;
}

/** V70 / V115: a status date can't be before the plan was signed or after today (or the session day, if later). */
export function statusDateRange(opts: {
  planSignedAt?: string | null;
  sessionDate?: string;
  today: string;
}): StatusRange {
  const day = (v: string | null | undefined) => (v ? String(v).slice(0, 10) : '');
  const session = day(opts.sessionDate);
  const today = day(opts.today);
  return { min: day(opts.planSignedAt), max: session && session > today ? session : today };
}

/** The help line under the date field (V115): "Can't be before 08/02/2026 or after 10/20/2026." */
export function statusRangeText(r: StatusRange): string {
  return r.min
    ? `Can't be before ${mdy(r.min)} or after ${mdy(r.max)}.`
    : `Can't be after ${mdy(r.max)}.`;
}

/** Null when the date is fine; else what is wrong with it. An empty date is fine (no date). */
export function statusDateError(iso: string, r: StatusRange): string | null {
  if (!iso) return null;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return 'Type the date as MM/DD/YYYY.';
  if ((r.min && iso < r.min) || iso > r.max) return statusRangeText(r);
  return null;
}

/**
 * The date a picked status starts with (V79 goalStatusDateInfo, without AI): a closing status kept
 * from the plan keeps the plan's date; anything else dated takes the session date; Not addressed
 * has none. Kept inside the range.
 */
export function defaultStatusDate(opts: {
  status: string;
  sessionDate?: string;
  planStatus?: string;
  planDate?: string;
  range: StatusRange;
}): string {
  if (!statusDated(opts.status)) return '';
  const closing = ['Met', 'Not met', 'Discontinued'];
  const pick =
    opts.status === opts.planStatus && closing.includes(opts.status) && opts.planDate
      ? opts.planDate
      : (opts.sessionDate ?? '').slice(0, 10) || opts.range.max;
  if (opts.range.min && pick < opts.range.min) return opts.range.min;
  if (pick > opts.range.max) return opts.range.max;
  return pick;
}

/** The caption under a set status: Same as plan, Changed from <plan status>, or Set this session. */
export function statusCaption(
  status: string | undefined,
  planStatus: string | undefined,
): { text: string; from?: string } | null {
  if (!status || status === NOT_ADDRESSED) return null;
  if (!planStatus) return { text: 'Set this session' };
  if (status === planStatus) return { text: 'Same as plan' };
  return { text: 'Changed from', from: planStatus };
}

/** One goal or objective row's status as the note keeps it. */
export interface RowStatus {
  status?: string;
  statusDate?: string;
  note?: string;
  /** The linked plan's own status for this goal or objective, when the plan has one. */
  planStatus?: string;
}

/** "N of M set": how many rows have a status this session. */
export function statusCounts(rows: RowStatus[]): { set: number; total: number } {
  return { set: rows.filter((r) => isGoalStatus(r.status)).length, total: rows.length };
}

/** The rows "Confirm the other N" would set: no status yet, and the plan has one to keep. */
export function confirmable(rows: RowStatus[]): number[] {
  return rows.flatMap((r, i) => (!r.status && isGoalStatus(r.planStatus) ? [i] : []));
}

/** A copied row starts without this session's status, date and note (a copy is a new session). */
export function withoutStatus<T extends RowStatus>(r: T): T {
  const rest = { ...r };
  delete rest.status;
  delete rest.statusDate;
  delete rest.note;
  return rest;
}

/** The saved goal-level statuses (body.goalStatuses), keeping only well-formed rows. */
export function readGoalLevels(v: unknown): ({ goalText: string } & RowStatus)[] {
  if (!Array.isArray(v)) return [];
  return v.flatMap((x) => {
    if (!x || typeof x !== 'object') return [];
    const o = x as Record<string, unknown>;
    if (typeof o.goalText !== 'string') return [];
    return [
      {
        goalText: o.goalText,
        ...(isGoalStatus(o.status) ? { status: o.status } : {}),
        ...(typeof o.statusDate === 'string' ? { statusDate: o.statusDate } : {}),
        ...(typeof o.note === 'string' && o.note ? { note: o.note } : {}),
        ...(typeof o.planStatus === 'string' ? { planStatus: o.planStatus } : {}),
      },
    ];
  });
}

/** "In progress · 10/10/2026. Note: ..." for the printout and the preview. */
export function statusLine(r: RowStatus): string {
  if (!isGoalStatus(r.status)) return '';
  return `${statusLabel(r.status, r.statusDate)}${r.note ? `. Note: ${r.note}` : ''}`;
}
